export function decodeErrorResponse(response: Response): Promise<string> {
    return response.text().then((text) => {
        const data = JSON.parse(text);
        const errors = data.errors.map((error: { message: string }) => error.message).join(", ");
        throw new Error(errors);
    });
}

/**
 * The message a 400 came back with, or null for anything else.
 *
 * A 400 from these APIs is a RULE the operator violated, and the body
 * (`{errors:[{message}]}`) names which one. Swallowing it and showing "please try
 * again" is not a smaller failure than showing the wrong message — it is a dead
 * end: the operator retries the same input forever because nothing ever tells
 * them what is wrong. It also means every rule the server gains that the client
 * does not mirror is invisible from the day it ships.
 *
 * Deliberately narrow to 400. A 500 is a fault, not a rejection, and its message
 * is for the log — the caller keeps its own generic handling for those.
 */
export function serverRejectionMessage(error: unknown): string | null {
    if (typeof error !== "object" || error === null) return null;
    const { status, data } = error as { status?: unknown; data?: unknown };
    if (status !== 400) return null;
    if (typeof data !== "object" || data === null) return null;
    const { errors } = data as { errors?: unknown };
    if (!Array.isArray(errors) || errors.length === 0) return null;
    const messages = errors
        .map((entry) => (entry as { message?: unknown }).message)
        .filter((message): message is string => typeof message === "string" && message.length > 0);
    return messages.length > 0 ? messages.join(" ") : null;
}
